'use client';

import { useQueryClient } from '@tanstack/react-query';

import {
  acknowledgeAlertAction,
  bulkAlertActionAction,
  dismissAlertAction,
} from '@/features/admin-dashboard/actions/systemAlertsActions';
import {
  ActivityItem,
  DashboardMetrics,
  OrderReminderStatusUi,
  SystemAlert,
} from '@/features/admin-dashboard/types';
import { AdminMealSelectionDataResponse } from '@/features/admin-meal-booking/types';
import { createCanteen } from '@/features/canteen-management/actions/createCanteenAction';
import {
  type DeleteCanteenResult,
  deleteCanteen,
} from '@/features/canteen-management/actions/deleteCanteenAction';
import { updateCanteen } from '@/features/canteen-management/actions/updateCanteenAction';
import { CanteenUi } from '@/features/canteen-management/types';
import { createCompany } from '@/features/company-management/actions/createCompanyAction';
import { deleteCompany } from '@/features/company-management/actions/deleteCompanyAction';
import { updateCompany } from '@/features/company-management/actions/updateCompanyAction';
import { CompanyUI } from '@/features/company-management/types';
import {
  createDishCategoryAction,
  deleteDishCategoryAction,
  updateDishCategoryAction,
} from '@/features/custom-dish-categories/actions';
import { DishCategoryUI } from '@/features/custom-dish-categories/types';
import { fetchDishCategories } from '@/features/custom-dish-categories/utils';
import { DietaryRestrictionUI } from '@/features/dietary-restrictions/types';
import { createDish } from '@/features/dish-management/actions/createDishAction';
import {
  type DeleteDishResult,
  deleteDish,
} from '@/features/dish-management/actions/deleteDishAction';
import { restoreDish } from '@/features/dish-management/actions/restoreDishAction';
import { updateDish } from '@/features/dish-management/actions/updateDishAction';
import { DishUi } from '@/features/dish-management/types';
import { TodayMealSummary } from '@/features/employee-dashboard/types';
import { fetchTodayMeals } from '@/features/employee-dashboard/utils/fetchTodayMeals';
import { EmployeeOrdersResponse, fetchEmployeeOrders } from '@/features/employee-orders';
import type { FoodWasteData, FoodWasteQuery } from '@/features/food-waste/types';
import {
  MealRatingDaily,
  MealRatingDailyParams,
  MealRatingStats,
  PendingMealRating,
} from '@/features/meal-rating/types';
import { bulkUpdateEmployees } from '@/features/user-management/actions/bulkUpdateEmployeeAction';
import { deleteEmployee } from '@/features/user-management/actions/deleteEmployeeAction';
import { deleteEmployeeOrdersForWeek } from '@/features/user-management/actions/deleteEmployeeOrdersAction';
import { updateEmployee } from '@/features/user-management/actions/updateEmployeeAction';
import { EmployeeOrderUi, EmployeeUi } from '@/features/user-management/types';
import { publishWeeklyMenuAction } from '@/features/weekly-menu-creation/actions/publishWeeklyMenuAction';
import { unpublishWeeklyMenuAction } from '@/features/weekly-menu-creation/actions/unpublishWeeklyMenuAction';
import { WeeklyMenuUI } from '@/features/weekly-menu-creation/types';
import { MealTime } from '@/lib/enums';
import { RegistryEntry, SingleObjectRegistryEntry } from '@/lib/tanstackQuery/types';
import {
  AvailableDish,
  DistributionKPIs,
  fetchActivityFeed,
  fetchAvailableDishes,
  fetchCanteenById,
  fetchCanteens,
  fetchCompanies,
  fetchCompanyById,
  fetchDashboardMetrics,
  fetchDietaryRestrictionById,
  fetchDietaryRestrictions,
  fetchDishById,
  fetchDishes,
  fetchDistributionKPIs,
  fetchEmployeeById,
  fetchEmployees,
  fetchMealRatingStats,
  fetchMealTranslations,
  fetchOrderReminderStatus,
  fetchPendingMealRatings,
  fetchSystemAlertById,
  fetchSystemAlerts,
  fetchTenants,
  fetchUnreadCount,
  fetchUserTenant,
  fetchWeeklyMenuById,
  fetchWeeklyMenus,
  MealTranslation,
  TenantUi,
} from '@/lib/tanstackQuery/utils';
import { formatDateOnly } from '@/lib/utils/date-only';

// Central registry for query keys and mutation server actions
export const queryRegistry: {
  dishes: RegistryEntry<DishUi, DishUi, DishUi, any, DeleteDishResult>;
  companies: RegistryEntry<CompanyUI, CompanyUI, CompanyUI, any, CompanyUI>;
  canteens: RegistryEntry<CanteenUi, CanteenUi, CanteenUi, any, DeleteCanteenResult>;
  employees: RegistryEntry<EmployeeUi, EmployeeUi, EmployeeUi, any, { id: string }>;
  'meal-translations': RegistryEntry<MealTranslation, MealTranslation, MealTranslation>;
  'order-reminder-status': RegistryEntry<
    OrderReminderStatusUi,
    OrderReminderStatusUi,
    OrderReminderStatusUi
  >;
  tenants: RegistryEntry<TenantUi, TenantUi, TenantUi>;
  'user-tenant': SingleObjectRegistryEntry<TenantUi, TenantUi, TenantUi>;
  'dietary-restrictions': RegistryEntry<
    DietaryRestrictionUI,
    DietaryRestrictionUI,
    DietaryRestrictionUI
  >;
  'dish-categories': RegistryEntry<DishCategoryUI, DishCategoryUI, DishCategoryUI>;
  'dashboard-metrics': SingleObjectRegistryEntry<
    DashboardMetrics,
    DashboardMetrics,
    DashboardMetrics
  >;
  'system-alerts': RegistryEntry<SystemAlert, SystemAlert, SystemAlert>;
  'activity-feed': RegistryEntry<ActivityItem, ActivityItem, ActivityItem>;

  'weekly-menus': RegistryEntry<WeeklyMenuUI, WeeklyMenuUI, WeeklyMenuUI>;
  'employee-dashboard-today-meals': SingleObjectRegistryEntry<
    TodayMealSummary,
    TodayMealSummary,
    TodayMealSummary
  >;
  'distribution-kpis': SingleObjectRegistryEntry<
    DistributionKPIs,
    DistributionKPIs,
    DistributionKPIs
  >;
  'available-dishes': SingleObjectRegistryEntry<AvailableDish[], AvailableDish[], AvailableDish[]>;
  'employee-orders': RegistryEntry<EmployeeOrderUi, EmployeeOrderUi, EmployeeOrderUi>;
  'employee-orders-history': SingleObjectRegistryEntry<
    EmployeeOrdersResponse,
    EmployeeOrdersResponse,
    EmployeeOrdersResponse
  >;
  'admin-meal-selection': RegistryEntry<
    AdminMealSelectionDataResponse,
    AdminMealSelectionDataResponse,
    AdminMealSelectionDataResponse
  >;
  'meal-rating-stats': RegistryEntry<MealRatingStats, MealRatingStats, MealRatingStats>;
  'meal-rating-daily': RegistryEntry<MealRatingDailyParams, MealRatingDailyParams, MealRatingDaily>;
  'meal-rating-pending': RegistryEntry<PendingMealRating, PendingMealRating, PendingMealRating>;
  'notification-unread-count': SingleObjectRegistryEntry<number, number, number>;
  'food-waste': RegistryEntry<FoodWasteQuery, FoodWasteQuery, FoodWasteData>;
} = {
  dishes: {
    queryKey: (...args: unknown[]) => {
      const [id, locale] = args as [string | number | undefined, string | undefined];
      const baseKey = id ? ['dishes', id] : ['dishes'];
      return locale ? [...baseKey, locale] : baseKey;
    },
    queryFn: () => fetchDishes(),
    detailQueryFn: fetchDishById,
    mutations: {
      create: createDish,
      update: updateDish,
      delete: deleteDish,
      restore: restoreDish,
    },
  },
  companies: {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['companies', id] : ['companies'];
    },
    queryFn: fetchCompanies,
    detailQueryFn: fetchCompanyById,
    mutations: {
      create: createCompany,
      update: updateCompany,
      delete: deleteCompany,
    },
  },
  canteens: {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['canteens', id] : ['canteens'];
    },
    queryFn: fetchCanteens,
    detailQueryFn: fetchCanteenById,
    mutations: {
      create: createCanteen,
      update: updateCanteen,
      delete: deleteCanteen,
    },
  },
  employees: {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['employees', id] : ['employees'];
    },
    queryFn: fetchEmployees,
    detailQueryFn: fetchEmployeeById,
    mutations: {
      // Note: updateEmployee and bulkUpdateEmployees now return enhanced result types
      // with canteen transfer error handling. The hooks use these directly.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      update: updateEmployee as any,
      delete: deleteEmployee,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      bulkUpdate: bulkUpdateEmployees as any,
    },
  },
  'meal-translations': {
    queryKey: ['meal-translations'],
    queryFn: fetchMealTranslations,
    mutations: {},
  },
  'order-reminder-status': {
    queryKey: ['order-reminder-status'],
    queryFn: fetchOrderReminderStatus,
    mutations: {},
    cacheConfig: {
      // Read through getEnhancedQueryConfig in useIncompleteEmployees; the plain
      // getQueryConfig drops this block. Changes only when an admin nudges
      // someone (the nudge invalidates this key directly) or a user enables
      // push, so a minute of staleness plus a refetch on focus is enough.
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
    },
  },
  'employee-dashboard-today-meals': {
    queryKey: ['employee-dashboard', 'today-meals'],
    queryFn: fetchTodayMeals,
    mutations: {},
  },
  'distribution-kpis': {
    queryKey: (...args: unknown[]) => {
      const [canteenId, mealTime, date] = args as [
        string | undefined,
        'lunch' | 'dinner' | undefined,
        Date | undefined,
      ];
      if (!canteenId || !mealTime || !date) {
        return ['distribution-kpis'];
      }
      // Kiosk-local calendar date, the same value the record and the dish picker use
      return ['distribution-kpis', canteenId, mealTime, formatDateOnly(date)];
    },
    queryFn: () => {
      throw new Error('Distribution KPIs query requires canteenId, mealTime, and date parameters');
    },
    detailQueryFn: (combinedId: string | number) => {
      // Combined ID format: "canteenId|mealTime|YYYY-MM-DD"
      const [canteenId, mealTime, dateStr] = String(combinedId).split('|');
      return fetchDistributionKPIs(canteenId, mealTime as 'lunch' | 'dinner', dateStr);
    },
    mutations: {},
    // Cache configuration for dashboard metrics
    cacheConfig: {
      staleTime: 1 * 60 * 1000, // 1 minute - KPIs can be slightly stale
      refetchOnWindowFocus: true, // Refresh when user returns to window
      refetchOnMount: true, // Always fetch fresh data on component mount
      refetchInterval: 1 * 60 * 1000, // Auto-refresh every 1 minute
    },
  },
  'available-dishes': {
    queryKey: (...args: unknown[]) => {
      const [canteenId, date, daypart] = args as [
        string | undefined,
        string | undefined,
        MealTime | undefined,
      ];
      if (!canteenId || !date || !daypart) {
        return ['available-dishes'];
      }
      return ['available-dishes', canteenId, date, daypart];
    },
    queryFn: () => {
      throw new Error('Available dishes query requires canteenId, date and daypart');
    },
    detailQueryFn: (combinedId: string | number) => {
      // Combined ID format: "canteenId|YYYY-MM-DD|daypart"
      const [canteenId, date, daypart] = String(combinedId).split('|');
      return fetchAvailableDishes(canteenId, date, daypart as MealTime);
    },
    mutations: {},
    cacheConfig: {
      staleTime: 5 * 60 * 1000, // the day's menu does not change during service
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchInterval: false,
    },
  },
  tenants: {
    queryKey: ['tenants'],
    queryFn: fetchTenants,
    mutations: {},
  },
  'user-tenant': {
    queryKey: ['user-tenant'],
    queryFn: fetchUserTenant,
    mutations: {},
  },
  'dietary-restrictions': {
    queryKey: (...args: unknown[]) => {
      const [id, language] = args as [string | number | undefined, 'en' | 'pt' | undefined];
      const baseKey = id ? ['dietary-restrictions', id] : ['dietary-restrictions'];
      return language && language !== 'en' ? [...baseKey, language] : baseKey;
    },
    queryFn: () => fetchDietaryRestrictions('en'), // Default to English for backward compatibility
    detailQueryFn: (id: string | number) => fetchDietaryRestrictionById(id, 'en'), // Default to English
    mutations: {},
  },
  'dish-categories': {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['dish-categories', id] : ['dish-categories'];
    },
    queryFn: fetchDishCategories,
    mutations: {
      create: createDishCategoryAction,
      update: updateDishCategoryAction,
      delete: deleteDishCategoryAction,
    },
  },
  'dashboard-metrics': {
    queryKey: ['dashboard-metrics'],
    queryFn: fetchDashboardMetrics,
    mutations: {},
    // Cache configuration for dashboard metrics
    cacheConfig: {
      staleTime: 5 * 60 * 1000, // 5 minutes - metrics can be slightly stale
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for quick access
      refetchOnWindowFocus: true, // Refresh when user returns to dashboard
      refetchOnMount: true, // Always fetch fresh data on component mount
      refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    },
    // Error handling and retry configuration
    errorConfig: {
      retry: 3, // Retry failed requests up to 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      retryCondition: (error: any) => {
        // Retry on network errors and 5xx server errors, but not on 4xx client errors
        return !error.response || error.response.status >= 500;
      },
    },
    // Cache invalidation strategies
    invalidationConfig: {
      // Invalidate dashboard metrics when related data changes
      invalidateOn: [
        'companies', // When companies are created/updated/deleted
        'canteens', // When canteens are created/updated/deleted
        'employees', // When employees are created/updated
        // 'meal-orders', // When orders are placed (commented out - key doesn't exist yet)
      ],
      // Background refetch on related mutations
      backgroundRefetchOn: [
        'user-registration',
        'company-creation',
        'canteen-creation',
        'order-placement',
      ],
    },
  },
  'system-alerts': {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['system-alerts', id] : ['system-alerts'];
    },
    queryFn: fetchSystemAlerts,
    detailQueryFn: fetchSystemAlertById,
    mutations: {
      acknowledge: acknowledgeAlertAction,
      dismiss: dismissAlertAction,
      bulkAction: bulkAlertActionAction,
    },
    // Cache configuration for system alerts
    cacheConfig: {
      staleTime: 30 * 1000, // 30 seconds - alerts should be fresh
      gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for quick access
      refetchOnWindowFocus: true, // Refresh when user returns to check alerts
      refetchOnMount: true, // Always fetch fresh alerts on component mount
      refetchInterval: 60 * 1000, // Auto-refresh every minute for real-time updates
    },
    // Error handling and retry configuration
    errorConfig: {
      retry: 2, // Retry failed requests up to 2 times (less than metrics)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Faster retry for alerts
      retryCondition: (error: any) => {
        // Retry on network errors and 5xx server errors, but not on 4xx client errors
        return !error.response || error.response.status >= 500;
      },
    },
    // Cache invalidation strategies
    invalidationConfig: {
      // Invalidate system alerts when related data changes
      invalidateOn: [
        'companies', // When companies have issues
        'canteens', // When canteens have issues
        'employees', // When user-related alerts occur
        'dishes', // When dish-related alerts occur
      ],
      // Background refetch on related mutations
      backgroundRefetchOn: [
        'company-creation',
        'canteen-creation',
        'user-registration',
        'dish-creation',
        'order-placement',
      ],
    },
  },
  'activity-feed': {
    queryKey: (...args: unknown[]) => {
      const [params] = args as [import('@/lib/tanstackQuery/utils').ActivityFeedParams | undefined];
      if (params) {
        // Create a stable key based on parameters
        const keyParts = ['activity-feed'];
        if (params.page) keyParts.push('page', params.page.toString());
        if (params.limit) keyParts.push('limit', params.limit.toString());
        if (params.type) keyParts.push('type', params.type);
        if (params.actor_id) keyParts.push('actor', params.actor_id);
        if (params.target_type) keyParts.push('target', params.target_type);
        if (params.date_from) keyParts.push('from', params.date_from);
        if (params.date_to) keyParts.push('to', params.date_to);
        if (params.search) keyParts.push('search', params.search);
        return keyParts;
      }
      return ['activity-feed'];
    },
    queryFn: async () => {
      const response = await fetchActivityFeed();
      return response.data;
    },
    detailQueryFn: async (id: string | number) => {
      const response = await fetchActivityFeed({ limit: 100 });
      return response.data.find((activity) => activity.id === String(id)) || null;
    },
    mutations: {
      // Activity feed is read-only, no mutations needed
    },
    // Cache configuration for activity feed
    cacheConfig: {
      staleTime: 2 * 60 * 1000, // 2 minutes - activity should be relatively fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for navigation
      refetchOnWindowFocus: true, // Refresh when user returns to see new activity
      refetchOnMount: true, // Always fetch fresh activity on component mount
      refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes for real-time updates
    },
    // Error handling and retry configuration
    errorConfig: {
      retry: 3, // Retry failed requests up to 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000), // Exponential backoff
      retryCondition: (error: any) => {
        // Retry on network errors and 5xx server errors, but not on 4xx client errors
        return !error.response || error.response.status >= 500;
      },
    },
    // Cache invalidation strategies
    invalidationConfig: {
      // Invalidate activity feed when any data changes (since all changes create activity)
      invalidateOn: ['companies', 'canteens', 'employees', 'dishes', 'system-alerts'],
      // Background refetch on all mutations since they create activity
      backgroundRefetchOn: [
        'company-creation',
        'company-update',
        'company-deletion',
        'canteen-creation',
        'canteen-update',
        'canteen-deletion',
        'user-registration',
        'user-update',
        'dish-creation',
        'dish-update',
        'dish-deletion',
        'order-placement',
        'order-update',
        'order-cancellation',
        'alert-creation',
        'alert-acknowledgment',
        'alert-dismissal',
      ],
    },
  },

  'weekly-menus': {
    queryKey: (...args: unknown[]) => {
      const [id] = args as [string | number | undefined];
      return id ? ['weekly-menus', id] : ['weekly-menus'];
    },
    queryFn: fetchWeeklyMenus,
    detailQueryFn: fetchWeeklyMenuById,
    mutations: {
      publish: publishWeeklyMenuAction,
      unpublish: unpublishWeeklyMenuAction,
    },
  },
  'employee-orders': {
    queryKey: (...args: unknown[]) => {
      const [weekStartDate] = args as [string | undefined];
      if (!weekStartDate) {
        throw new Error('Employee orders query requires weekStartDate parameter');
      }
      return ['employee-orders', weekStartDate];
    },
    queryFn: () => {
      throw new Error('Employee orders query requires weekStartDate parameter');
    },
    mutations: {
      deleteOrders: deleteEmployeeOrdersForWeek,
    },
  },
  'employee-orders-history': {
    queryKey: ['employee-orders-history'],
    queryFn: fetchEmployeeOrders,
    mutations: {},
  },
  'meal-rating-stats': {
    queryKey: (...args: unknown[]) => {
      const [mealId] = args as [string | undefined];
      return mealId ? ['meal-rating-stats', mealId] : ['meal-rating-stats'];
    },
    queryFn: () => fetchMealRatingStats(),
    mutations: {},
    cacheConfig: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  },
  'meal-rating-daily': {
    queryKey: (...args: unknown[]) => {
      const [params] = args as [MealRatingDailyParams | undefined];
      if (!params) return ['meal-rating-daily'];
      return [
        'meal-rating-daily',
        params.startDate,
        params.endDate,
        params.mealId ?? 'all-meals',
        params.canteenId ?? 'all-canteens',
      ];
    },
    queryFn: () => {
      throw new Error('Meal rating daily query requires date range parameters');
    },
    mutations: {},
    cacheConfig: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  },
  'meal-rating-pending': {
    queryKey: (...args: unknown[]) => {
      const [limit] = args as [number | undefined];
      return limit ? ['meal-rating-pending', limit] : ['meal-rating-pending'];
    },
    queryFn: () => fetchPendingMealRatings(),
    mutations: {},
    cacheConfig: {
      staleTime: 30 * 1000,
      gcTime: 2 * 60 * 1000,
    },
  },
  'notification-unread-count': {
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    mutations: {},
    cacheConfig: {
      staleTime: 15 * 1000,
      gcTime: 60 * 1000,
      refetchInterval: 30 * 1000,
    },
  },
  'admin-meal-selection': {
    queryKey: (...args: unknown[]) => {
      const [targetUserId, weekStartDate, locale] = args as [
        string | undefined,
        string | undefined,
        string | undefined,
      ];
      // Return base key only when no args provided (for invalidation)
      if (!targetUserId && !weekStartDate && !locale) {
        return ['admin-meal-selection'];
      }
      return ['admin-meal-selection', targetUserId, weekStartDate, locale];
    },
    queryFn: () => {
      throw new Error(
        'Admin meal selection query requires targetUserId, weekStartDate, and locale parameters',
      );
    },
    mutations: {},
  },
  // Read through fetchFoodWasteDataAction in useFoodWasteData; nothing is written.
  'food-waste': {
    queryKey: (...args: unknown[]) => {
      const [query] = args as [FoodWasteQuery | undefined];
      if (!query) return ['food-waste'];
      return [
        'food-waste',
        query.canteenId,
        query.companyId,
        query.period,
        query.from ?? '',
        query.to ?? '',
      ];
    },
    queryFn: () => {
      throw new Error('Food waste query requires canteen and period parameters');
    },
    mutations: {},
    cacheConfig: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
};

// Helper to retrieve a query key by name, supporting dynamic arguments (e.g. id)
export function getQueryKey(key: keyof typeof queryRegistry, ...args: unknown[]): unknown[] {
  const entry = queryRegistry[key];
  if (!entry) throw new Error(`No queryKey found for "${key}"`);
  if (typeof entry.queryKey === 'function') {
    return entry.queryKey(...args);
  }
  return entry.queryKey;
}

/**
 * Helper to get enhanced query config with cache, error, and invalidation settings.
 * Returns configuration object that can be spread into useQuery options.
 */
export function getEnhancedQueryConfig<K extends keyof typeof queryRegistry>(
  key: K,
  id?: string | number,
) {
  const entry = queryRegistry[key];
  if (!entry) throw new Error(`No query config found for "${key}"`);

  const baseConfig = getQueryConfig(key, id as any);

  return {
    ...baseConfig,
    // Apply cache configuration
    ...(entry.cacheConfig && {
      staleTime: entry.cacheConfig.staleTime,
      gcTime: entry.cacheConfig.gcTime,
      refetchOnWindowFocus: entry.cacheConfig.refetchOnWindowFocus,
      refetchOnMount: entry.cacheConfig.refetchOnMount,
      refetchInterval: entry.cacheConfig.refetchInterval,
    }),
    // Apply error handling configuration
    ...(entry.errorConfig && {
      retry: entry.errorConfig.retry,
      retryDelay: entry.errorConfig.retryDelay,
    }),
    // Custom error handling
    onError: (error: any) => {
      console.error(`Query error for ${String(key)}:`, error);
      // Additional error handling can be added here
    },
    // Success callback for logging
    onSuccess: (data: any) => {
      console.debug(`Query success for ${String(key)}:`, data);
    },
  };
}

/**
 * Helper to invalidate related queries based on invalidation configuration.
 * Should be called after mutations that affect dashboard metrics.
 */
export function useInvalidateRelatedQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateMetrics: async () => {
      const metricsEntry = queryRegistry['dashboard-metrics'];
      if (metricsEntry?.invalidationConfig?.invalidateOn) {
        // Invalidate all related queries
        for (const relatedKey of metricsEntry.invalidationConfig.invalidateOn) {
          await queryClient.invalidateQueries({
            queryKey: getQueryKey(relatedKey as keyof typeof queryRegistry),
          });
        }
      }
      // Always invalidate dashboard metrics
      await queryClient.invalidateQueries({
        queryKey: getQueryKey('dashboard-metrics'),
      });
    },

    backgroundRefreshMetrics: () => {
      // Trigger background refetch without invalidating cache
      queryClient.refetchQueries({
        queryKey: getQueryKey('dashboard-metrics'),
        type: 'active',
      });
    },
  };
}

/**
 * Specialized hook for dashboard metrics with enhanced error handling and retry logic.
 * This hook should be used instead of the generic getQueryConfig for dashboard metrics.
 */
export function useDashboardMetricsQuery() {
  const config = getEnhancedQueryConfig('dashboard-metrics');

  return {
    ...config,
    // Additional error handling specific to dashboard metrics
    onError: (error: any) => {
      console.error('Dashboard metrics query failed:', error);

      // Log specific error types for monitoring
      if (error?.response?.status >= 500) {
        console.error('Server error loading dashboard metrics:', error);
      } else if (error?.response?.status === 403) {
        console.error('Permission denied for dashboard metrics:', error);
      } else if (!navigator.onLine) {
        console.error('Network offline - dashboard metrics unavailable:', error);
      }
    },

    // Success callback with performance logging
    onSuccess: (data: DashboardMetrics) => {
      console.debug('Dashboard metrics loaded successfully:', {
        totalUsers: data.totalUsers,
        totalCompanies: data.totalCompanies,
        totalCanteens: data.totalCanteens,
        weeklyOrders: data.weeklyOrders,
      });
    },

    // Network mode configuration
    networkMode: 'online' as const, // Only fetch when online

    // Additional retry configuration for dashboard metrics
    retryOnMount: true,

    // Placeholder data while loading
    placeholderData: {
      totalUsers: 0,
      activeUsers: 0,
      totalCompanies: 0,
      activeCompanies: 0,
      totalCanteens: 0,
      activeCanteens: 0,
      weeklyOrders: 0,
      monthlyRevenue: 0,
      todaysGuests: {
        lunch: 0,
        dinner: 0,
        total: 0,
      },
      pendingOrders: {
        count: 0,
        description: 'No pending orders',
        incompleteCount: 0,
        noSelectionCount: 0,
      },
      menuSchedule: {
        currentWeek: '',
        nextWeekNeeded: '',
        weeksAhead: 0,
      },
      previousPeriodComparison: {
        users: 0,
        companies: 0,
        canteens: 0,
        orders: 0,
        revenue: 0,
      },
      trends: {
        users: [],
        orders: [],
        revenue: [],
      },
    } as DashboardMetrics,
  };
}

/**
 * Specialized hook for activity feed with enhanced error handling and pagination support.
 * This hook should be used instead of the generic getQueryConfig for activity feed.
 */
export function useActivityFeedQuery(
  params: import('@/lib/tanstackQuery/utils').ActivityFeedParams = {},
) {
  const entry = queryRegistry['activity-feed'];

  return {
    queryKey: typeof entry.queryKey === 'function' ? entry.queryKey(params) : entry.queryKey,
    queryFn: () => fetchActivityFeed(params),

    // Cache configuration
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes

    // Error handling
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 15000),

    // Additional error handling specific to activity feed
    onError: (error: any) => {
      console.error('Activity feed query failed:', error);

      // Log specific error types for monitoring
      if (error?.response?.status >= 500) {
        console.error('Server error loading activity feed:', error);
      } else if (error?.response?.status === 403) {
        console.error('Permission denied for activity feed:', error);
      } else if (!navigator.onLine) {
        console.error('Network offline - activity feed unavailable:', error);
      }
    },

    // Success callback with performance logging
    onSuccess: (data: import('@/lib/tanstackQuery/utils').ActivityFeedResponse) => {
      console.debug('Activity feed loaded successfully:', {
        totalActivities: data.pagination.total,
        currentPage: data.pagination.page,
        hasMore: data.pagination.hasMore,
        activitiesCount: data.data.length,
      });
    },

    // Network mode configuration
    networkMode: 'online' as const, // Only fetch when online

    // Additional retry configuration for activity feed
    retryOnMount: true,

    // Placeholder data while loading
    placeholderData: {
      success: true,
      data: [],
      pagination: {
        page: params.page || 1,
        limit: params.limit || 20,
        total: 0,
        hasMore: false,
        totalPages: 0,
      },
    } as import('@/lib/tanstackQuery/utils').ActivityFeedResponse,
  };
}

/**
 * Helper to get query config for dietary restrictions with language support.
 * Maintains backward compatibility while adding language parameter support.
 */
export function getDietaryRestrictionsQueryConfig(
  language: 'en' | 'pt' = 'en',
  id?: string | number,
) {
  const entry = queryRegistry['dietary-restrictions'];
  if (!entry) throw new Error('No query config found for dietary-restrictions');

  if (id !== undefined) {
    return {
      queryKey:
        typeof entry.queryKey === 'function'
          ? entry.queryKey(id, language)
          : [...entry.queryKey, id, language],
      queryFn: () => fetchDietaryRestrictionById(id, language),
    };
  }

  return {
    queryKey:
      typeof entry.queryKey === 'function'
        ? entry.queryKey(undefined, language)
        : [...entry.queryKey, language],
    queryFn: () => fetchDietaryRestrictions(language),
  };
}

/**
 * Helper to get query config for dishes with locale support.
 * Maintains backward compatibility while adding locale parameter support.
 */
export function getDishesQueryConfig(locale?: string): {
  queryKey: unknown[];
  queryFn: () => Promise<DishUi[]>;
};
export function getDishesQueryConfig(
  locale: string | undefined,
  id: string | number,
): {
  queryKey: unknown[];
  queryFn: () => Promise<DishUi | null>;
};
export function getDishesQueryConfig(
  locale?: string,
  id?: string | number,
): {
  queryKey: unknown[];
  queryFn: () => Promise<DishUi[] | DishUi | null>;
} {
  const entry = queryRegistry['dishes'];
  if (!entry) throw new Error('No query config found for dishes');

  if (id !== undefined) {
    return {
      queryKey:
        typeof entry.queryKey === 'function'
          ? entry.queryKey(id, locale)
          : [...entry.queryKey, id, locale],
      queryFn: () => fetchDishById(id, locale),
    };
  }

  return {
    queryKey:
      typeof entry.queryKey === 'function'
        ? entry.queryKey(undefined, locale)
        : [...entry.queryKey, locale],
    queryFn: () => fetchDishes(locale),
  };
}

/**
 * Helper to get query config for list or detail queries.
 * If id is provided, returns detail query config, else list query config.
 */
type DetailQueryConfig<K extends keyof typeof queryRegistry> = {
  queryKey: unknown[];
  queryFn: () => Promise<
    ReturnType<NonNullable<(typeof queryRegistry)[K]['detailQueryFn']>> extends Promise<infer R>
      ? R
      : never
  >;
};

type ListQueryConfig<K extends keyof typeof queryRegistry> = {
  queryKey: unknown[];
  queryFn: (typeof queryRegistry)[K]['queryFn'];
};

export function getQueryConfig<K extends keyof typeof queryRegistry>(key: K): ListQueryConfig<K>;
export function getQueryConfig<K extends keyof typeof queryRegistry>(
  key: K,
  id: string | number,
): DetailQueryConfig<K>;
export function getQueryConfig<K extends keyof typeof queryRegistry>(
  key: K,
  id?: string | number,
): any {
  const entry = queryRegistry[key];
  if (!entry) throw new Error(`No query config found for "${key}"`);
  if (id !== undefined && entry.detailQueryFn) {
    return {
      queryKey: typeof entry.queryKey === 'function' ? entry.queryKey(id) : [...entry.queryKey, id],
      queryFn: () => entry.detailQueryFn!(id),
    };
  }
  return {
    queryKey: typeof entry.queryKey === 'function' ? entry.queryKey() : entry.queryKey,
    queryFn: entry.queryFn,
  };
}

/**
 * React hook to execute a registered server action and invalidate the relevant query keys.
 * By default, it invalidates both the list and (if id is provided) the detail key.
 * Also handles dashboard metrics invalidation based on invalidation configuration.
 */
export function useServerActionMutation<
  K extends keyof typeof queryRegistry,
  M extends keyof NonNullable<(typeof queryRegistry)[K]['mutations']>,
>(key: K, mutationType: M): NonNullable<(typeof queryRegistry)[K]['mutations']>[M] {
  const queryClient = useQueryClient();
  const { invalidateMetrics, backgroundRefreshMetrics } = useInvalidateRelatedQueries();
  const entry = queryRegistry[key];
  const mutation = entry?.mutations?.[mutationType as keyof typeof entry.mutations] as
    | NonNullable<(typeof queryRegistry)[K]['mutations']>[M]
    | undefined;
  if (!entry || !mutation)
    throw new Error(
      `No serverAction of type "${String(mutationType)}" registered for "${String(key)}"`,
    );

  return (async (args: unknown) => {
    console.log(`Executing server action: ${String(key)}.${String(mutationType)}`, args);

    try {
      const result = await (mutation as (...args: unknown[]) => unknown)(args);
      console.log(`Server action success: ${String(key)}.${String(mutationType)}`, result);

      // Only invalidate queries if the mutation was successful
      console.log(`Invalidating queries for: ${String(key)}`);

      // Handle special case for employee-orders with weekStartDate
      if (key === 'employee-orders' && args && (args as { weekStartDate?: string }).weekStartDate) {
        await queryClient.invalidateQueries({
          queryKey: getQueryKey(key, (args as { weekStartDate: string }).weekStartDate),
        });
      } else if (key !== 'employee-orders') {
        // Only call general invalidation for non-employee-orders
        await queryClient.invalidateQueries({ queryKey: getQueryKey(key) });
      }

      // Keep the existing id-based invalidation logic
      if (args && (args as { id?: string | number }).id) {
        await queryClient.invalidateQueries({
          queryKey: getQueryKey(key, (args as { id?: string | number }).id),
        });
      }

      // Check if dashboard metrics should be invalidated based on this mutation
      const metricsEntry = queryRegistry['dashboard-metrics'];
      if (metricsEntry?.invalidationConfig?.invalidateOn?.includes(String(key))) {
        console.log('Invalidating dashboard metrics');
        await invalidateMetrics();
      }

      // Check if dashboard metrics should be background refreshed
      const mutationEvent = `${String(key)}-${String(mutationType)}`;
      if (metricsEntry?.invalidationConfig?.backgroundRefetchOn?.includes(mutationEvent)) {
        console.log('Background refreshing dashboard metrics');
        backgroundRefreshMetrics();
      }

      console.log(`Server action completed successfully: ${String(key)}.${String(mutationType)}`);
      return result;
    } catch (error) {
      console.error(`Server action error for ${String(key)}.${String(mutationType)}:`, error);
      console.error('Error details:', {
        message: (error as Error)?.message,
        cause: (error as Error)?.cause,
        stack: (error as Error)?.stack,
      });
      throw error; // Re-throw the error so TanStack Query can handle it
    }
  }) as NonNullable<(typeof queryRegistry)[K]['mutations']>[M];
}
